<script lang="ts">
  import { onDestroy, tick } from 'svelte'
  import { get } from 'svelte/store'
  import { push, querystring } from 'svelte-spa-router'
  import { locale, tStore } from '../../i18n'
  import { aiAnalysisApi } from '../../api/aiAnalysis'
  import { analysisOutcome, createAnalysisPoller } from '../../api/aiAnalysis/poller'
  import type { AiAnalysisStatus } from '../../api/aiAnalysis/types'
  import {
    procurementsApi,
    type Attachment,
    type ProcurementDetail,
    type ProcurementSourceRef,
  } from '../../api/procurements'
  import ExpandableText from '../../components/ExpandableText.svelte'
  import PageHeader from '../../components/PageHeader.svelte'
  import StatusBadge from '../../components/StatusBadge.svelte'
  import UnavailableNotice from '../../components/UnavailableNotice.svelte'
  import {
    aiScoreColor,
    countryAlpha2,
    formatDateTime,
    formatValue,
    formatFileSize,
  } from '../../composables/format'
  import { useAbortable } from '../../composables/useAbortable'
  import { useTicker } from '../../composables/useTicker'
  import { requestNotificationRefresh } from '../../stores/inAppNotifications'
  import {
    criteriaErrorMessage,
    criterionEvidence,
    criterionReason,
    criteriaDiff,
    historyScoreIsHighlighted,
    retryEstimateMessage,
    scoreChangeExplanation,
    sourceStatusBadge,
    sourceStatusMessage,
  } from './procurementDetail'

  interface Props {
    params?: { id?: string }
  }

  let { params }: Props = $props()

  const { start } = useAbortable()

  let detail = $state<ProcurementDetail | null>(null)
  let loading = $state(true)
  let errorMsg = $state<string | null>(null)

  let aiTriggering = $state(false)
  let aiTriggered = $state(false)
  let aiError = $state<string | null>(null)
  let aiPolling = $state(false)

  // Collapse state for the criteria breakdown and attachments lists. Both start
  // collapsed; the toggles only render once their section has content.
  let criteriaOpen = $state(false)
  let aiScoreTab = $state<'overview' | 'history'>('overview')
  let selectedHistoryId = $state<number | null>(null)
  let expandedCriteriaDiffId = $state<number | null>(null)
  let attachmentsOpen = $state(false)
  let attachmentOpeningId = $state<number | null>(null)
  let attachmentError = $state<string | null>(null)

  // Drives the rate-limit retry estimate below — retryEstimateMessage counts
  // down to a fixed deadline, so without something ticking, a page left open
  // would keep showing the delay computed at load time forever instead of
  // counting down (or disappearing once the deadline passes).
  const RETRY_ESTIMATE_TICK_MS = 30_000
  let retryEstimateNow = $state(new Date())
  const retryEstimateTicker = useTicker(RETRY_ESTIMATE_TICK_MS, () => {
    retryEstimateNow = new Date()
  })
  onDestroy(retryEstimateTicker.stop)

  // The AI-score banner shows only a fixed translated message for a
  // rate-limited criteria-match failure (see the 'rate_limited' branch
  // below) — the technical detail goes to the console instead of the DOM.
  $effect(() => {
    const cm = detail?.criteriaMatch
    if (cm?.status === 'failed' && cm.errorCode === 'rate_limited') {
      console.error('EOJN document download failed', {
        code: cm.errorCode,
        reason: cm.reason,
        retryAfterSeconds: cm.retryAfterSeconds,
        httpStatus: 429,
        rawError: cm.error,
      })
    }
  })

  function analysisSettled(d: ProcurementDetail | null): boolean {
    const terminal = (status: string | null | undefined) =>
      status === 'completed' || status === 'failed' || status === 'cancelled'
    return terminal(d?.criteriaMatch?.status) && terminal(d?.aiAnalysis?.status)
  }

  const analysisPoller = createAnalysisPoller()
  onDestroy(() => analysisPoller.dispose())
  let trackedId: number | null = null
  let aiProgress = $state<AiAnalysisStatus | null>(null)

  function stopPolling() {
    if (trackedId !== null) analysisPoller.stop(trackedId)
    trackedId = null
    aiPolling = false
    aiProgress = null
  }

  function startPolling(id: number) {
    stopPolling()
    trackedId = id
    aiPolling = true
    analysisPoller.track(id, (status) => {
      aiProgress = status
      const outcome = status ? analysisOutcome(status) : null
      if (status && !outcome) return
      // A missing batch-status row can mean this procurement was deleted. On
      // its detail page, stop polling and re-fetch instead of inventing a status.
      stopPolling()
      if (outcome === 'success') requestNotificationRefresh()
      // Load the full result only when the calculation has finished.
      void procurementsApi
        .get(id)
        .then((next) => {
          if (Number(params?.id) === id) detail = next
        })
        .catch(() => {})
    })
  }

  $effect(() => {
    const id = Number(params?.id)
    stopPolling()
    aiTriggered = false
    aiError = null
    aiScoreTab = 'overview'
    selectedHistoryId = null
    expandedCriteriaDiffId = null
    if (!Number.isFinite(id)) {
      detail = null
      errorMsg = $tStore('views.procurementDetail.notFound')
      loading = false
      return
    }
    void load(id)
    return () => stopPolling()
  })

  async function load(id: number) {
    const signal = start()
    loading = true
    errorMsg = null
    try {
      detail = await procurementsApi.get(id, { signal })
      // Deep-link support: arriving with `?focus=ai-score` (e.g. from clicking a
      // score in the dashboard table) jumps straight to the AI score section
      // once it has rendered.
      if (!signal.aborted) void focusSection()
      // The server may already be processing an analysis this component never
      // triggered itself (started from the dashboard/bulk run, or from an
      // earlier visit to this same page) — aiPolling only reflects runs *this*
      // component started, so without this check the Calculate button would
      // stay wrongly enabled and Cancel would never appear. Poll whenever
      // either sub-analysis is still running, regardless of who started it.
      if (
        !signal.aborted &&
        (detail.criteriaMatch?.status === 'processing' ||
          detail.aiAnalysis?.status === 'processing')
      ) {
        startPolling(id)
      }
    } catch (err) {
      if (signal.aborted) return // navigated away before the detail arrived
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : String(err)
      errorMsg = message
    } finally {
      if (!signal.aborted) loading = false
    }
  }

  // Maps a `?focus=` value to the element id to scroll to. Kept as an explicit
  // allow-list so a stray/unknown value can't drive arbitrary scrolling.
  const FOCUS_TARGETS: Record<string, string> = { 'ai-score': 'ai-score' }

  async function focusSection() {
    const focus = new URLSearchParams(get(querystring) ?? '').get('focus')
    const targetId = focus ? FOCUS_TARGETS[focus] : undefined
    if (!targetId) return
    // Wait for the just-set `detail` to render the section, then let layout
    // settle a frame before scrolling so the destination is measured correctly.
    await tick()
    requestAnimationFrame(() => {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  async function triggerAiAnalysis() {
    const id = Number(params?.id)
    if (!Number.isFinite(id) || aiTriggering || aiPolling) return
    aiTriggering = true
    aiError = null
    try {
      await aiAnalysisApi.trigger(id)
      aiTriggered = true
      // Silent refetch — going through load() would flip `loading=true` and
      // briefly collapse the page to a single line, dropping scroll position.
      try {
        detail = await procurementsApi.get(id)
      } catch {
        // The trigger already succeeded; ignore the refresh blip and let the
        // polling loop pick up the result.
      }
      // The trigger only enqueues work — poll until both the score and the
      // description are written so the user sees them without manually
      // refreshing.
      if (!analysisSettled(detail)) {
        startPolling(id)
      } else {
        requestNotificationRefresh()
      }
    } catch (err) {
      aiError = errorMessage(err) || $tStore('views.procurementDetail.aiScore.triggerFailed')
    } finally {
      aiTriggering = false
    }
  }

  // Cooperative cancel, mirroring the dashboard's cancelAi: the backend either
  // records a fresh 'cancelled' result (first-ever run) or reverts to the
  // tender's previous result — the poll loop is only stopped once the cancel
  // call itself has succeeded, so a failed cancel (e.g. network blip) leaves
  // polling in place to keep tracking the still-presumably-running analysis.
  async function cancelAiAnalysis() {
    const id = Number(params?.id)
    if (!Number.isFinite(id) || !aiPolling) return
    try {
      await aiAnalysisApi.cancel(id)
      stopPolling()
      detail = await procurementsApi.get(id)
    } catch (err) {
      aiError = errorMessage(err) || $tStore('views.procurementDetail.aiScore.triggerFailed')
    }
  }

  function errorMessage(err: unknown): string {
    if (err && typeof err === 'object' && 'message' in err) {
      return String((err as { message: unknown }).message)
    }
    return String(err)
  }

  async function openAttachment(event: MouseEvent, attachment: Attachment) {
    event.preventDefault()
    if (attachmentOpeningId !== null) return

    attachmentOpeningId = attachment.id
    attachmentError = null

    const opensInline = isPdfAttachment(attachment)
    const popup = opensInline ? window.open('about:blank', '_blank') : null
    if (popup) popup.opener = null

    try {
      const file = await procurementsApi.downloadAttachment(attachment)
      const objectUrl = URL.createObjectURL(
        blobWithType(file.blob, file.contentType ?? attachment.contentType),
      )
      if (opensInline) {
        openObjectUrl(objectUrl, popup)
        scheduleObjectUrlRevoke(objectUrl)
      } else {
        popup?.close()
        downloadObjectUrl(objectUrl, attachment.filename)
        scheduleObjectUrlRevoke(objectUrl)
      }
    } catch (err) {
      popup?.close()
      attachmentError = errorMessage(err)
    } finally {
      attachmentOpeningId = null
    }
  }

  function isPdfAttachment(attachment: Attachment): boolean {
    const contentType = normalizedContentType(attachment.contentType)
    return contentType === 'application/pdf' || attachment.filename.toLowerCase().endsWith('.pdf')
  }

  function normalizedContentType(contentType: string | null): string {
    return contentType?.split(';')[0]?.trim().toLowerCase() ?? ''
  }

  function blobWithType(blob: Blob, contentType: string | null): Blob {
    return blob.type || !contentType ? blob : new Blob([blob], { type: contentType })
  }

  function openObjectUrl(objectUrl: string, popup: Window | null) {
    if (popup) {
      popup.location.href = objectUrl
      return
    }
    const opened = window.open(objectUrl, '_blank', 'noopener,noreferrer')
    if (!opened) {
      window.location.assign(objectUrl)
    }
  }

  function downloadObjectUrl(objectUrl: string, filename: string) {
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  function scheduleObjectUrlRevoke(objectUrl: string) {
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 300_000)
  }

  // Canonical TED (Tenders Electronic Daily) notice page for a publication
  // number (e.g. "445852-2026"). Only reached when d.tedId is non-null.
  function tedUrl(tedId: string): string {
    return `https://ted.europa.eu/en/notice/-/detail/${encodeURIComponent(tedId)}`
  }

  function sourceRefs(d: ProcurementDetail): ProcurementSourceRef[] {
    return d.sourceRefs?.length
      ? d.sourceRefs
      : [
          {
            sourceId: d.sourceId,
            sourceName: d.sourceName,
            externalId: d.externalId,
            tedId: d.tedId ?? null,
            url: d.url,
            errorKind: d.criteriaMatch?.errorKind ?? null,
          },
        ]
  }

  function sourceLabel(name: string): string {
    return name.trim().toUpperCase()
  }

  function tedSourceUrl(d: ProcurementDetail): string | null {
    const ref = sourceRefs(d).find((r) => r.sourceName.toLowerCase() === 'ted')
    if (ref?.url) return ref.url
    const tedId = ref?.tedId ?? d.tedId
    return tedId ? tedUrl(tedId) : null
  }

  function goBack() {
    if (window.history.length > 1) {
      window.history.back()
    } else {
      /* No history to pop to (e.g. the detail page was opened directly) —
         land on the actual tender list route; '/procurements' isn't a route. */
      push('/')
    }
  }

  // English title. Prefer the dedicated `titleEng` column from listing data, and
  // fall back to the raw listing payload's NameENG for rows persisted before
  // that column existed / not yet re-crawled. Null when neither is present.
  function englishTitle(d: ProcurementDetail): string | null {
    if (d.titleEng && d.titleEng.trim() !== '') return d.titleEng
    const raw = d.rawPayload as { listing_row?: { NameENG?: unknown } } | null | undefined
    const v = raw?.listing_row?.NameENG
    return typeof v === 'string' && v.trim() !== '' ? v : null
  }

  /*
   * Overview description. Prefer the extracted description and fall back to
   * the listing title so the Overview card never shows a blank field.
   */
  function overviewText(d: ProcurementDetail): string | null {
    return d.description ?? d.title ?? null
  }

  function statusTone(s: string | null) {
    switch (s) {
      case 'published':
        return 'info' as const
      case 'in_review':
        return 'warning' as const
      case 'awarded':
        return 'success' as const
      case 'cancelled':
        return 'danger' as const
      case 'closed':
      case 'unknown':
      default:
        return 'neutral' as const
    }
  }

  function asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null
  }

  function stringField(record: Record<string, unknown> | null, key: string): string | null {
    const value = record?.[key]
    return typeof value === 'string' && value.trim() ? value.trim() : null
  }

  function extractScoringCriteria(requiredCriteria: unknown, activeLocale: string): string | null {
    const record = asRecord(requiredCriteria)
    if (activeLocale === 'en') {
      return stringField(record, 'scoring_criteria_en') ?? stringField(record, 'scoring_criteria')
    }
    return stringField(record, 'scoring_criteria') ?? stringField(record, 'scoring_criteria_en')
  }

  function selectAiScoreTab(tab: 'overview' | 'history') {
    aiScoreTab = tab
    // Entering or leaving history always restores its default focus state.
    selectedHistoryId = null
    expandedCriteriaDiffId = null
  }

  function toggleCriteriaDiff(entryId: number, current: boolean) {
    expandedCriteriaDiffId = expandedCriteriaDiffId === entryId ? null : entryId
    selectedHistoryId = current ? null : entryId
  }
</script>

<section class="detail">
  <div class="detail__back">
    <button class="btn btn--secondary btn--sm" onclick={goBack}>
      ← {$tStore('common.back')}
    </button>
  </div>

  {#if loading}
    <p class="muted">{$tStore('views.procurementDetail.loading')}</p>
  {:else if errorMsg || !detail}
    <p class="error">{errorMsg ?? $tStore('views.procurementDetail.notFound')}</p>
  {:else}
    {@const d = detail}
    <PageHeader title={d.title} subtitle={d.externalId}>
      {#snippet actions()}
        {@const tedLink = tedSourceUrl(d)}
        {#if d.archived}
          <StatusBadge label={$tStore('views.procurementDetail.archivedBadge')} tone="neutral" />
        {/if}
        {#if d.url && d.url !== tedLink}
          <a
            class="btn btn--secondary detail__action"
            href={d.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {$tStore('common.openInSource')} ↗
          </a>
        {/if}
        {#if d.originalUrl && d.originalUrl !== d.url && d.originalUrl !== tedLink}
          <a
            class="btn btn--secondary detail__action"
            href={d.originalUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {$tStore('common.openOriginalNotice')} ↗
          </a>
        {/if}
        {#if tedLink}
          <a
            class="btn btn--secondary detail__action"
            href={tedLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {$tStore('common.openOnTed')} ↗
          </a>
        {:else}
          <!--
            Not on TED: render a disabled button, wrapped in a span that carries
            the tooltip. Disabled buttons swallow mouse events in some browsers,
            so the native title on the button alone wouldn't show on hover — the
            wrapper is what surfaces the explanation.
          -->
          <span
            class="detail__action-wrap"
            title={$tStore('views.procurementDetail.notOnTedTooltip')}
          >
            <button
              type="button"
              class="btn btn--secondary detail__action"
              disabled
              aria-label={$tStore('views.procurementDetail.notOnTedTooltip')}
            >
              {$tStore('common.openOnTed')} ↗
            </button>
          </span>
        {/if}
      {/snippet}
    </PageHeader>

    <div class="detail__grid">
      <article class="card">
        <h3>{$tStore('views.procurementDetail.sectionOverview')}</h3>
        <dl class="kv">
          <dt>{$tStore('views.procurementDetail.fields.id')}</dt>
          <dd class="mono">{d.externalId}</dd>

          <dt>{$tStore('views.procurementDetail.fields.tedId')}</dt>
          <dd class="mono">
            {#if d.tedId}
              {d.tedId}
            {:else}
              <span class="muted">{$tStore('views.procurementDetail.notOnTed')}</span>
            {/if}
          </dd>

          <dt>{$tStore('views.procurementDetail.fields.description')}</dt>
          <dd class="description">
            {#if overviewText(d)}
              <ExpandableText text={overviewText(d) ?? ''} />
            {:else}
              —
            {/if}
          </dd>

          <dt>{$tStore('views.procurementDetail.fields.descriptionEng')}</dt>
          <dd>{englishTitle(d) ?? '—'}</dd>

          <dt>{$tStore('views.procurementDetail.fields.buyer')}</dt>
          <dd>{d.buyerName ?? '—'}</dd>

          <dt>{$tStore('views.procurementDetail.fields.buyerId')}</dt>
          <dd class="mono">{d.buyerId ?? '—'}</dd>

          <dt>{$tStore('views.procurementDetail.fields.value')}</dt>
          <dd>{formatValue(d.valueEur, d.valueOriginal, d.currency)}</dd>

          <dt>{$tStore('views.procurementDetail.fields.status')}</dt>
          <dd>
            {#if d.status}
              <StatusBadge
                label={$tStore(`views.procurements.status.${d.status}`)}
                tone={statusTone(d.status)}
              />
            {:else}
              —
            {/if}
          </dd>

          <dt>{$tStore('views.procurementDetail.fields.type')}</dt>
          <dd>
            {d.procurementType ? $tStore(`views.procurements.type.${d.procurementType}`) : '—'}
          </dd>

          {#if d.country}
            <dt>{$tStore('views.procurementDetail.fields.country')}</dt>
            <dd><span class="country-badge">{countryAlpha2(d.country)}</span></dd>
          {/if}

          <dt>{$tStore('views.procurementDetail.fields.publishedAt')}</dt>
          <dd>{formatDateTime(d.publishedAt)}</dd>

          <dt>{$tStore('views.procurementDetail.fields.deadlineAt')}</dt>
          <dd>{formatDateTime(d.deadlineAt)}</dd>

          <dt>{$tStore('views.procurementDetail.fields.cpvCodes')}</dt>
          <dd>
            {#if d.cpvCodes.length === 0}
              —
            {:else}
              <div class="cpv-list">
                {#each d.cpvCodes as cpv (cpv)}
                  <span class="cpv">{cpv}</span>
                {/each}
              </div>
            {/if}
          </dd>

          <dt>{$tStore('views.procurementDetail.fields.source')}</dt>
          <dd>
            <div class="source-badges">
              {#each sourceRefs(d) as ref (`${ref.sourceId}-${ref.externalId}`)}
                <span class="source-badge" title={`${ref.sourceName}: ${ref.externalId}`}>
                  {sourceLabel(ref.sourceName)}
                </span>
              {/each}
            </div>
          </dd>
        </dl>
      </article>
    </div>

    <article class="card">
      <h3>{$tStore('views.procurementDetail.sectionSources')}</h3>
      <ul class="source-list">
        {#each sourceRefs(d) as ref (`${ref.sourceId}-${ref.externalId}`)}
          <li>
            <div class="source-list__main">
              <span class="source-badge">{sourceLabel(ref.sourceName)}</span>
              <span class="mono">{ref.externalId}</span>
              {#if ref.tedId}
                <span class="mono muted">{ref.tedId}</span>
              {/if}
            </div>
            {#if ref.url}
              <a href={ref.url} target="_blank" rel="noopener noreferrer">
                {$tStore('common.openInSource')} ↗
              </a>
            {/if}
            {#if ref.errorKind}
              {@const badge = sourceStatusBadge(ref.errorKind, $tStore)}
              <span title={badge.tooltip}>
                <StatusBadge label={badge.label} tone="warning" />
              </span>
            {/if}
          </li>
        {/each}
      </ul>
    </article>

    {#if !d.archived}
      <div class="ai-actions">
        <button
          class="btn {d.criteriaMatch ? 'btn--secondary' : 'btn--primary'} ai-score__button"
          onclick={triggerAiAnalysis}
          disabled={aiTriggering || aiPolling}
        >
          {#if aiTriggering || aiPolling}
            <span class="spinner spinner--sm" aria-hidden="true"></span>
            {$tStore('views.procurementDetail.aiScore.calculating')}
          {:else if d.criteriaMatch}
            {$tStore('views.procurementDetail.aiScore.recalculate')}
          {:else}
            {$tStore('views.procurementDetail.aiScore.calculate')}
          {/if}
        </button>
        {#if aiPolling && aiProgress?.phase}
          <span role="status">
            {$tStore('views.dashboard.aiPhase.' + aiProgress.phase)}
            {#if aiProgress.retryAt}
              ({new Date(aiProgress.retryAt).toLocaleTimeString()})
            {/if}
          </span>
        {/if}
        {#if aiPolling}
          <button
            type="button"
            class="btn btn--secondary ai-score__cancel"
            onclick={cancelAiAnalysis}
            title={$tStore('views.dashboard.cancelAiTooltip')}
          >
            {$tStore('views.dashboard.cancelAi')}
          </button>
        {/if}
        {#if aiError}
          <p class="error">{aiError}</p>
        {/if}
      </div>
    {/if}

    <article class="card ai-score-anchor" id="ai-score">
      <h3>{$tStore('views.procurementDetail.aiScore.section')}</h3>
      <div
        class="ai-score-tabs"
        role="tablist"
        aria-label={$tStore('views.procurementDetail.aiScore.section')}
      >
        <button
          type="button"
          role="tab"
          class="ai-score-tabs__tab"
          class:ai-score-tabs__tab--active={aiScoreTab === 'overview'}
          aria-selected={aiScoreTab === 'overview'}
          onclick={() => selectAiScoreTab('overview')}
        >
          {$tStore('views.procurementDetail.aiScore.overviewTab')}
        </button>
        <button
          type="button"
          role="tab"
          class="ai-score-tabs__tab"
          class:ai-score-tabs__tab--active={aiScoreTab === 'history'}
          aria-selected={aiScoreTab === 'history'}
          onclick={() => selectAiScoreTab('history')}
        >
          {$tStore('views.procurementDetail.aiScore.historyTab')} ({d.aiScoreHistory.length})
        </button>
      </div>

      {#if aiScoreTab === 'overview'}
        <div class="ai-score" role="tabpanel">
          {#if d.criteriaMatch?.status === 'completed' && d.criteriaMatch.error === 'no_criteria_documents'}
            <!-- A completed run with no criteria-relevant documents to score
                 stores a placeholder 0% match_percentage — the sentinel above
                 is what actually distinguishes this from a real 0% match, so
                 it must be checked before the "completed" branch below, which
                 would otherwise render that placeholder as a real score. -->
            <p class="muted">{$tStore('views.procurementDetail.aiScore.noDocuments')}</p>
          {:else if d.criteriaMatch?.status === 'completed' && d.criteriaMatch.matchPercentage !== null}
            {@const detail = d.criteriaMatch.matchDetail ?? []}
            {@const met = detail.filter((c) => c.satisfied)}
            {@const unmet = detail.filter((c) => !c.satisfied)}
            <div class="ai-score__value">
              <span
                class="ai-score__number"
                style:color={aiScoreColor(d.criteriaMatch.matchPercentage)}
                >{d.criteriaMatch.matchPercentage}</span
              >
              <span class="muted">%</span>
            </div>
            <span class="muted">
              {$tStore('views.procurementDetail.aiScore.matchLabel')}
            </span>

            {@const scoringText = extractScoringCriteria(d.criteriaMatch.requiredCriteria, $locale)}
            {#if scoringText}
              <div class="criteria-summary">
                <h4>{$tStore('views.procurementDetail.aiScore.scoringCriteria')}</h4>
                <p>{scoringText}</p>
              </div>
            {/if}

            {#if detail.length === 0}
              <p class="muted">
                {$tStore('views.procurementDetail.aiScore.noMatchableCriteria')}
              </p>
            {/if}

            {#if detail.length > 0}
              <button
                class="collapse__toggle"
                type="button"
                aria-expanded={criteriaOpen}
                onclick={() => (criteriaOpen = !criteriaOpen)}
              >
                <span class="collapse__chevron" class:collapse__chevron--open={criteriaOpen}>▶</span
                >
                <span>
                  {$tStore('views.procurementDetail.aiScore.criteriaToggle')} ({detail.length})
                </span>
              </button>
              {#if criteriaOpen}
                <div class="criteria">
                  {#if met.length > 0}
                    <div class="criteria__group">
                      <h4 class="criteria__title criteria__title--met">
                        {$tStore('views.procurementDetail.aiScore.criteriaMet')} ({met.length})
                      </h4>
                      <ul class="criteria__list">
                        {#each met as c, i (i)}
                          {@const evidence = criterionEvidence(c)}
                          <li class="criteria__item">
                            <div class="criteria__head">
                              <span class="criteria__icon criteria__icon--met" aria-hidden="true"
                                >✓</span
                              >
                              <span class="criteria__name">{c.criterion}</span>
                              <span class="criteria__cat">
                                {$tStore(`views.procurementDetail.aiScore.category.${c.category}`)}
                              </span>
                            </div>
                            <p class="criteria__just">
                              {criterionReason(c, $tStore)}
                            </p>
                            {#if evidence}
                              <figure class="criteria__evidence">
                                <figcaption class="criteria__evidence-label">
                                  {$tStore('views.procurementDetail.aiScore.evidenceLabel')}
                                </figcaption>
                                <blockquote class="criteria__evidence-quote">{evidence}</blockquote>
                              </figure>
                            {/if}
                          </li>
                        {/each}
                      </ul>
                    </div>
                  {/if}

                  {#if unmet.length > 0}
                    <div class="criteria__group">
                      <h4 class="criteria__title criteria__title--unmet">
                        {$tStore('views.procurementDetail.aiScore.criteriaUnmet')} ({unmet.length})
                      </h4>
                      <ul class="criteria__list">
                        {#each unmet as c, i (i)}
                          <li class="criteria__item">
                            <div class="criteria__head">
                              <span class="criteria__icon criteria__icon--unmet" aria-hidden="true"
                                >✗</span
                              >
                              <span class="criteria__name">{c.criterion}</span>
                              <span class="criteria__cat">
                                {$tStore(`views.procurementDetail.aiScore.category.${c.category}`)}
                              </span>
                            </div>
                            <p class="criteria__just">
                              {criterionReason(c, $tStore)}
                            </p>
                          </li>
                        {/each}
                      </ul>
                    </div>
                  {/if}
                </div>
              {/if}
            {/if}
          {:else if d.criteriaMatch?.status === 'processing' || aiTriggered || aiPolling}
            <div class="ai-score__loader" role="status" aria-live="polite">
              <span class="spinner" aria-hidden="true"></span>
              <span class="muted">{$tStore('views.procurementDetail.aiScore.calculating')}</span>
            </div>
          {:else if d.criteriaMatch?.status === 'failed'}
            {#if d.criteriaMatch.errorCode === 'rate_limited'}
              {@const retryEstimate = d.criteriaMatch.retryAfterUntil
                ? retryEstimateMessage(d.criteriaMatch.retryAfterUntil, $tStore, retryEstimateNow)
                : null}
              <p class="error">
                {criteriaErrorMessage($tStore)}
                {#if retryEstimate}
                  <br />
                  <span class="muted">{retryEstimate}</span>
                {/if}
              </p>
            {:else if d.criteriaMatch.errorKind}
              <UnavailableNotice
                message={sourceStatusMessage(d.criteriaMatch.errorKind, $tStore)}
                detail={d.criteriaMatch.error}
              />
            {:else}
              <p class="error">
                {d.criteriaMatch.error ?? $tStore('views.procurementDetail.aiScore.failed')}
              </p>
            {/if}
          {:else if d.criteriaMatch?.status === 'cancelled'}
            <p class="muted">{$tStore('views.procurementDetail.aiScore.cancelled')}</p>
          {:else}
            <p class="muted">{$tStore('views.procurementDetail.aiScore.empty')}</p>
          {/if}
        </div>
      {:else}
        <div class="score-history" role="tabpanel">
          {#if d.aiScoreHistory.length === 0}
            <p class="muted">{$tStore('views.procurementDetail.aiScore.historyEmpty')}</p>
          {:else}
            {#each d.aiScoreHistory as entry, index (entry.id)}
              {@const highlighted = historyScoreIsHighlighted(
                entry.id,
                entry.current,
                selectedHistoryId,
              )}
              {@const previousEntry = d.aiScoreHistory[index + 1]}
              {@const changeExplanation = scoreChangeExplanation(entry, previousEntry, $tStore)}
              {@const changes = criteriaDiff(entry, previousEntry)}
              {@const changeGroups = [
                {
                  key: 'views.procurementDetail.aiScore.historyCriteriaAdded',
                  items: changes.added,
                  tone: 'added',
                },
                {
                  key: 'views.procurementDetail.aiScore.historyCriteriaRemoved',
                  items: changes.removed,
                  tone: 'removed',
                },
                {
                  key: 'views.procurementDetail.aiScore.historyCriteriaNewlyMatched',
                  items: changes.newlyMatched,
                  tone: 'matched',
                },
                {
                  key: 'views.procurementDetail.aiScore.historyCriteriaNoLongerMatched',
                  items: changes.noLongerMatched,
                  tone: 'unmatched',
                },
              ]}
              {@const criteriaChangeCount = changeGroups.reduce(
                (count, group) => count + group.items.length,
                0,
              )}
              {@const hasCriteriaChanges = Boolean(changeExplanation) && criteriaChangeCount > 0}
              {@const criteriaDiffExpanded = expandedCriteriaDiffId === entry.id}
              <div class="score-history__entry" class:score-history__entry--muted={!highlighted}>
                <button
                  type="button"
                  class="score-history__select"
                  aria-pressed={highlighted}
                  onclick={() => (selectedHistoryId = entry.current ? null : entry.id)}
                >
                  <span class="score-history__head">
                    <span class="score-history__score-wrap">
                      <span
                        class="score-history__score"
                        style:color={highlighted ? aiScoreColor(entry.matchPercentage) : null}
                        >{entry.matchPercentage}%</span
                      >
                      {#if entry.current}
                        <span class="score-history__current">
                          {$tStore('views.procurementDetail.aiScore.historyCurrent')}
                        </span>
                      {/if}
                    </span>
                    <span class="score-history__date">
                      {$tStore('views.procurementDetail.aiScore.historyCalculatedAt', {
                        date: formatDateTime(entry.calculatedAt),
                      })}
                    </span>
                  </span>

                  {#if changeExplanation}
                    <span class="score-history__analysis">
                      <span class="score-history__label">
                        {$tStore('views.procurementDetail.aiScore.historyChangeAnalysis')}
                      </span>
                      <span>{changeExplanation}</span>
                    </span>
                  {/if}
                </button>

                {#if hasCriteriaChanges}
                  <button
                    type="button"
                    class="score-history__diff-toggle"
                    aria-expanded={criteriaDiffExpanded}
                    aria-controls={`criteria-diff-${entry.id}`}
                    onclick={() => toggleCriteriaDiff(entry.id, entry.current)}
                  >
                    <span>
                      {$tStore('views.procurementDetail.aiScore.historyCriteriaChanges', {
                        count: criteriaChangeCount,
                      })}
                    </span>
                    <span
                      class="score-history__diff-chevron"
                      class:score-history__diff-chevron--open={criteriaDiffExpanded}
                      aria-hidden="true">▶</span
                    >
                  </button>
                {/if}

                {#if criteriaDiffExpanded && hasCriteriaChanges}
                  <div class="score-history__diff" id={`criteria-diff-${entry.id}`}>
                    {#each changeGroups as group (group.key)}
                      {#if group.items.length > 0}
                        <div class="score-history__diff-group">
                          <span class="score-history__diff-label">
                            {$tStore(group.key)}
                          </span>
                          <div class="score-history__criteria-changes">
                            {#each group.items as change (`${change.category}-${change.criterion}`)}
                              <span
                                class="score-history__criterion-change"
                                class:score-history__criterion-change--added={group.tone ===
                                  'added'}
                                class:score-history__criterion-change--removed={group.tone ===
                                  'removed'}
                                class:score-history__criterion-change--matched={group.tone ===
                                  'matched'}
                                class:score-history__criterion-change--unmatched={group.tone ===
                                  'unmatched'}
                              >
                                <span class="score-history__criterion-name">
                                  {#if group.tone === 'added'}<span aria-hidden="true">+</span>{/if}
                                  {#if group.tone === 'removed'}<span aria-hidden="true">−</span
                                    >{/if}
                                  {#if group.tone === 'matched'}<span aria-hidden="true">✓</span
                                    >{/if}
                                  {#if group.tone === 'unmatched'}<span aria-hidden="true">✗</span
                                    >{/if}
                                  {change.criterion}
                                </span>
                                <span class="score-history__criterion-category">
                                  {$tStore(
                                    `views.procurementDetail.aiScore.category.${change.category}`,
                                  )}
                                </span>
                              </span>
                            {/each}
                          </div>
                        </div>
                      {/if}
                    {/each}
                  </div>
                {/if}
              </div>
            {/each}
          {/if}
        </div>
      {/if}
    </article>

    <article class="card">
      <h3>{$tStore('views.procurementDetail.aiDescription.section')}</h3>
      {#if d.aiAnalysis?.status === 'completed'}
        {@const summary =
          $locale === 'en' && d.aiAnalysis.summaryEn
            ? d.aiAnalysis.summaryEn
            : d.aiAnalysis.summary}
        {@const keyRequirements =
          $locale === 'en' &&
          d.aiAnalysis.keyRequirementsEn &&
          d.aiAnalysis.keyRequirementsEn.length > 0
            ? d.aiAnalysis.keyRequirementsEn
            : d.aiAnalysis.keyRequirements}
        {#if summary}
          <p class="description">{summary}</p>
        {:else}
          <p class="muted">{$tStore('views.procurementDetail.aiDescription.noSummary')}</p>
        {/if}
        {#if keyRequirements && keyRequirements.length > 0}
          <h4 class="ai-description__subtitle">
            {$tStore('views.procurementDetail.aiDescription.keyRequirements')}
          </h4>
          <ul class="ai-description__list">
            {#each keyRequirements as req, i (i)}
              <li>{req}</li>
            {/each}
          </ul>
        {/if}
      {:else if d.aiAnalysis?.status === 'processing' || aiTriggering || aiPolling}
        <div class="ai-score__loader" role="status" aria-live="polite">
          <span class="spinner" aria-hidden="true"></span>
          <span class="muted">{$tStore('views.procurementDetail.aiDescription.writing')}</span>
        </div>
      {:else if d.aiAnalysis?.status === 'failed'}
        <p class="error">
          {d.aiAnalysis.error ?? $tStore('views.procurementDetail.aiDescription.failed')}
        </p>
      {:else if d.aiAnalysis?.status === 'cancelled'}
        <p class="muted">{$tStore('views.procurementDetail.aiDescription.cancelled')}</p>
      {:else}
        <p class="muted">{$tStore('views.procurementDetail.aiDescription.empty')}</p>
      {/if}
    </article>

    {#if !d.archived}
      <article class="card">
        <h3>{$tStore('views.procurementDetail.sectionAttachments')}</h3>
        {#if d.attachments.length === 0}
          {#if d.criteriaMatch?.status === 'completed'}
            <p class="muted">{$tStore('views.procurementDetail.noAttachments')}</p>
          {:else}
            <p class="muted">{$tStore('views.procurementDetail.noAttachmentsHint')}</p>
          {/if}
        {:else}
          <button
            class="collapse__toggle"
            type="button"
            aria-expanded={attachmentsOpen}
            onclick={() => (attachmentsOpen = !attachmentsOpen)}
          >
            <span class="collapse__chevron" class:collapse__chevron--open={attachmentsOpen}>▶</span>
            <span>
              {$tStore('views.procurementDetail.attachmentsToggle')} ({d.attachments.length})
            </span>
          </button>
          {#if attachmentError}
            <p class="error">{attachmentError}</p>
          {/if}
          {#if attachmentsOpen}
            <ul class="attachments">
              {#each d.attachments as att (att.id)}
                <li>
                  <a
                    href={att.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-busy={attachmentOpeningId === att.id}
                    onclick={(event) => openAttachment(event, att)}
                  >
                    {att.filename}
                  </a>
                  {#if attachmentOpeningId === att.id}
                    <span class="spinner spinner--sm" aria-hidden="true"></span>
                  {/if}
                  <span class="muted">
                    {att.contentType ?? ''} · {formatFileSize(att.fileSize)}
                  </span>
                </li>
              {/each}
            </ul>
          {/if}
        {/if}
      </article>
    {/if}

    <article class="card">
      <h3>{$tStore('views.procurementDetail.sectionMeta')}</h3>
      <dl class="kv">
        <dt>{$tStore('views.procurementDetail.fields.createdAt')}</dt>
        <dd>{formatDateTime(d.createdAt)}</dd>
        <dt>{$tStore('views.procurementDetail.fields.updatedAt')}</dt>
        <dd>{formatDateTime(d.updatedAt)}</dd>
      </dl>
    </article>
  {/if}
</section>

<style lang="scss">
  @use '../../assets/styles/variables' as v;

  .detail {
    display: flex;
    flex-direction: column;
    gap: v.$space-lg;
  }

  .detail__back {
    margin-bottom: -0.5rem;
  }

  // Header actions share a fixed min-width so "Open in source" and "Open on TED"
  // line up as equal-width buttons regardless of label length, and the disabled
  // TED button occupies the same footprint as an enabled one.
  .detail__action {
    min-width: 11rem;
  }

  .detail__action-wrap {
    display: inline-flex;
  }

  .detail__grid {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: v.$space-lg;

    @media (max-width: v.$bp-lg) {
      grid-template-columns: 1fr;
    }
  }

  .kv {
    display: grid;
    grid-template-columns: 12rem 1fr;
    gap: v.$space-sm v.$space-md;
    margin: 0;

    dt {
      color: v.$color-text-muted;
      font-size: 0.875rem;
    }

    dd {
      margin: 0;
    }

    @media (max-width: v.$bp-sm) {
      grid-template-columns: 1fr;

      dt {
        margin-top: v.$space-sm;
      }
    }
  }

  .mono {
    font-family: v.$font-mono;
    font-size: 0.875rem;
  }

  .description {
    margin: 0;
    white-space: pre-wrap;
  }

  .ai-description__subtitle {
    margin: v.$space-md 0 v.$space-xs;
    font-size: 0.875rem;
  }

  .ai-description__list {
    margin: 0;
    padding-left: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: v.$space-xs;
    font-size: 0.875rem;
  }

  .cpv-list {
    display: flex;
    flex-wrap: wrap;
    gap: v.$space-xs;
  }

  .cpv {
    padding: 0 v.$space-xs;
    border: 1px solid v.$color-border;
    border-radius: v.$radius-sm;
    font-family: v.$font-mono;
    font-size: 0.8125rem;
  }

  .source-badges {
    display: flex;
    flex-wrap: wrap;
    gap: v.$space-xs;
  }

  .country-badge {
    display: inline-block;
    padding: 0 v.$space-xs;
    border: 1px solid v.$color-border;
    border-radius: v.$radius-sm;
    background: v.$color-surface;
    color: v.$color-text-muted;
    font-family: v.$font-mono;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.45;
  }

  .source-badge {
    padding: 0 v.$space-xs;
    border: 1px solid #bfdbfe;
    border-radius: v.$radius-sm;
    background: #eff6ff;
    color: #1d4ed8;
    font-family: v.$font-mono;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.45;
  }

  .source-list {
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: v.$space-md;
      padding: v.$space-sm 0;
      border-bottom: 1px solid v.$color-border;

      &:last-child {
        border-bottom: none;
      }
    }
  }

  .source-list__main {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: v.$space-sm;
  }

  .ai-actions {
    display: flex;
    flex-direction: column;
    gap: v.$space-sm;
  }

  // Offset the deep-link jump target so it lands below the sticky header
  // instead of tucking under it.
  .ai-score-anchor {
    scroll-margin-top: calc(#{v.$header-height} + #{v.$space-md});
  }

  .ai-score-tabs {
    display: flex;
    gap: v.$space-md;
    margin: 0 0 v.$space-md;
    border-bottom: 1px solid v.$color-border;
  }

  .ai-score-tabs__tab {
    margin-bottom: -1px;
    padding: v.$space-xs 0 v.$space-sm;
    border: 0;
    border-bottom: 2px solid transparent;
    background: none;
    color: v.$color-text-muted;
    font: inherit;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      color: v.$color-primary;
    }

    &--active {
      border-bottom-color: v.$color-primary;
      color: v.$color-primary;
    }
  }

  .ai-score {
    display: flex;
    flex-direction: column;
    gap: v.$space-sm;
  }

  .ai-score__value {
    display: inline-flex;
    align-items: baseline;
    gap: v.$space-xs;
  }

  .ai-score__number {
    font-size: 2rem;
    font-weight: 600;
    color: v.$color-text;
    line-height: 1;
  }

  .ai-score__button {
    align-self: flex-start;
    width: auto;
    display: inline-flex;
    align-items: center;
    gap: v.$space-xs;
  }

  .ai-score__loader {
    display: inline-flex;
    align-items: center;
    gap: v.$space-sm;
    padding: v.$space-sm 0;
  }

  .score-history {
    display: flex;
    flex-direction: column;
    gap: v.$space-sm;
  }

  .score-history__entry {
    width: 100%;
    padding: v.$space-md;
    border: 1px solid v.$color-border;
    border-radius: v.$radius-sm;
    background: v.$color-surface-elevated;
    color: v.$color-text;
    transition:
      opacity 0.15s ease,
      border-color 0.15s ease;

    &:hover,
    &:focus-within {
      border-color: v.$color-primary;
    }

    &--muted {
      opacity: 0.58;
    }
  }

  .score-history__select {
    width: 100%;
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .score-history__head,
  .score-history__score-wrap,
  .score-history__analysis,
  .score-history__criteria-changes {
    display: flex;
  }

  .score-history__head {
    align-items: center;
    justify-content: space-between;
    gap: v.$space-md;
  }

  .score-history__score-wrap {
    align-items: center;
    gap: v.$space-sm;
  }

  .score-history__score {
    color: v.$color-text-subtle;
    font-size: 1.5rem;
    font-weight: 600;
    line-height: 1;
  }

  .score-history__current {
    padding: 1px v.$space-xs;
    border-radius: v.$radius-sm;
    background: v.$color-surface;
    color: v.$color-text-muted;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .score-history__date {
    color: v.$color-text-muted;
    font-size: 0.8125rem;
  }

  .score-history__analysis {
    flex-direction: column;
    gap: v.$space-xs;
    margin-top: v.$space-sm;
    color: v.$color-text-muted;
    font-size: 0.875rem;
  }

  .score-history__label {
    color: v.$color-text;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .score-history__criteria-changes {
    flex-wrap: wrap;
    gap: v.$space-xs;
  }

  .score-history__diff-toggle {
    display: flex;
    align-items: center;
    gap: v.$space-xs;
    width: fit-content;
    margin: v.$space-sm 0 0 auto;
    padding: v.$space-xs 0;
    border: 0;
    background: none;
    color: v.$color-primary;
    font: inherit;
    font-size: 0.8125rem;
    font-weight: 600;
    cursor: pointer;
  }

  .score-history__diff-chevron {
    display: inline-block;
    color: v.$color-text-muted;
    font-size: 0.6875rem;
    transition: transform 0.15s ease;

    &--open {
      transform: rotate(90deg);
    }
  }

  .score-history__diff {
    display: flex;
    flex-direction: column;
    gap: v.$space-sm;
    margin-top: v.$space-sm;
    padding-top: v.$space-sm;
    border-top: 1px solid v.$color-border;
  }

  .score-history__diff-group {
    display: flex;
    flex-direction: column;
    gap: v.$space-xs;
  }

  .score-history__diff-label {
    color: v.$color-text-muted;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .score-history__criterion-change {
    display: inline-flex;
    align-items: center;
    gap: v.$space-xs;
    padding: 1px v.$space-xs;
    border: 1px solid v.$color-border;
    border-radius: v.$radius-sm;
    background: v.$color-surface;
    color: v.$color-text;
    font-size: 0.8125rem;

    &--added,
    &--matched {
      color: v.$color-success-fg;
    }

    &--removed,
    &--unmatched {
      color: v.$color-danger-fg;
    }

    &--removed .score-history__criterion-name {
      text-decoration: line-through;
    }
  }

  .score-history__criterion-category {
    padding: 0 v.$space-xs;
    border-radius: v.$radius-sm;
    background: v.$color-surface-elevated;
    color: v.$color-text-muted;
    font-size: 0.6875rem;
    text-decoration: none;
  }

  @media (max-width: v.$bp-sm) {
    .score-history__head {
      align-items: flex-start;
      flex-direction: column;
      gap: v.$space-xs;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .score-history__entry,
    .score-history__diff-chevron {
      transition: none;
    }
  }

  .spinner {
    width: 1.25rem;
    height: 1.25rem;
    border: 2px solid v.$color-border;
    border-top-color: v.$color-primary;
    border-radius: 50%;
    animation: spinner-rotate 0.8s linear infinite;
    display: inline-block;
    flex-shrink: 0;

    &--sm {
      width: 0.875rem;
      height: 0.875rem;
      border-width: 2px;
    }
  }

  @keyframes spinner-rotate {
    to {
      transform: rotate(360deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .spinner {
      animation-duration: 2s;
    }
  }

  .collapse__toggle {
    display: inline-flex;
    align-items: center;
    gap: v.$space-xs;
    align-self: flex-start;
    padding: v.$space-xs 0;
    background: none;
    border: none;
    color: v.$color-text;
    font: inherit;
    font-weight: 600;
    font-size: 0.9375rem;
    cursor: pointer;

    &:hover {
      color: v.$color-primary;
    }
  }

  .collapse__chevron {
    display: inline-block;
    font-size: 0.75rem;
    color: v.$color-text-muted;
    transition: transform 0.15s ease;

    &--open {
      transform: rotate(90deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .collapse__chevron {
      transition: none;
    }
  }

  .criteria {
    display: flex;
    flex-direction: column;
    gap: v.$space-lg;
    margin-top: v.$space-sm;
  }

  .criteria-summary {
    padding: v.$space-sm;
    border: 1px solid v.$color-border;
    border-radius: v.$radius-sm;
    background: v.$color-surface-elevated;

    h4 {
      margin: 0 0 v.$space-xs;
      font-size: 0.9375rem;
    }

    p {
      margin: 0;
      color: v.$color-text-muted;
      font-size: 0.875rem;
    }
  }

  .criteria__group {
    display: flex;
    flex-direction: column;
    gap: v.$space-sm;
  }

  .criteria__title {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 600;

    &--met {
      color: v.$color-success-fg;
    }

    &--unmet {
      color: v.$color-danger-fg;
    }
  }

  .criteria__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: v.$space-xs;
  }

  .criteria__item {
    padding: v.$space-sm;
    border: 1px solid v.$color-border;
    border-radius: v.$radius-sm;
    background: v.$color-surface-elevated;
  }

  .criteria__head {
    display: flex;
    align-items: baseline;
    gap: v.$space-xs;
    flex-wrap: wrap;
  }

  .criteria__icon {
    font-weight: 700;

    &--met {
      color: v.$color-success-fg;
    }

    &--unmet {
      color: v.$color-danger-fg;
    }
  }

  .criteria__name {
    font-weight: 600;
  }

  .criteria__cat {
    margin-left: auto;
    padding: 0 v.$space-xs;
    border-radius: v.$radius-sm;
    background: v.$color-surface;
    color: v.$color-text-muted;
    font-size: 0.75rem;
    white-space: nowrap;
  }

  .criteria__just {
    margin: v.$space-xs 0 0;
    color: v.$color-text-muted;
    font-size: 0.875rem;
  }

  // Verbatim profile snippet backing a satisfied verdict. Rendered as a quote so
  // a reviewer can see exactly what the match was grounded in.
  .criteria__evidence {
    margin: v.$space-xs 0 0;
  }

  .criteria__evidence-label {
    color: v.$color-text-subtle;
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .criteria__evidence-quote {
    margin: 2px 0 0;
    padding: v.$space-xs v.$space-sm;
    border-left: 3px solid v.$color-border;
    background: v.$color-surface;
    border-radius: v.$radius-sm;
    color: v.$color-text-muted;
    font-size: 0.8125rem;
    font-style: italic;
  }

  .attachments {
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      display: flex;
      gap: v.$space-md;
      align-items: center;
      padding: v.$space-sm 0;
      border-bottom: 1px solid v.$color-border;

      &:last-child {
        border-bottom: none;
      }
    }
  }
</style>
